import { UpsertTestLocationDetailsPayload } from "../../../api/services/siteNpiDetails/siteNpiDetailsTypes";
import {
  AuthOnFile,
  TestLocationSubmissionDetails,
  TestLocationSubmissionPayload,
} from "../../../data-model/medicalPaOrdersModel";

/** Site location fields collected in the Add Site NPI modal. */
export interface SiteDetailsFormValues {
  /** Site type. */
  type: string;
  /** Site alias identifier. */
  alias: string;
  /** National Provider Identifier. */
  npi: string;
  /** Site display name. */
  site_name: string;
  /** Street address. */
  address: string;
  /** City. */
  city: string;
  /** State or province code. */
  state: string;
  /** Postal / ZIP code. */
  zip: string;
}

/** Stored Type value that hides the location fields and allows save. */
export const INTERNAL_SITE_TYPE = "internal";

/**
 * Display-only marker for a missing site detail.
 * It is never a stored catalog value.
 */
export const SITE_DETAILS_MISSING_FIELD_PLACEHOLDER = "-";

/**
 * Returns whether a site detail is empty or the missing-field placeholder.
 *
 * @param value - Current field value.
 * @returns True when the trimmed value is empty or `"-"`.
 */
export const isBlankSiteDetailsValue = (value: string): boolean => {
  const trimmed = value.trim();
  return (
    trimmed.length === 0 || trimmed === SITE_DETAILS_MISSING_FIELD_PLACEHOLDER
  );
};

/**
 * Trims a site detail and turns the missing-field placeholder into an empty string.
 *
 * @param value - Current field value.
 * @returns Trimmed value, or `""` when the value is `"-"`.
 */
export const blankIfMissingSiteDetailsPlaceholder = (value: string): string =>
  isBlankSiteDetailsValue(value) ? "" : value.trim();

/** Form fields required for an external site before Save is enabled. */
export const SITE_DETAILS_REQUIRED_FIELDS: (keyof SiteDetailsFormValues)[] = [
  "npi",
  "site_name",
];

/**
 * Returns whether the site type is internal.
 *
 * @param type - Current Type field value.
 * @returns True when the trimmed value is `internal`.
 */
export const isInternalSiteType = (type: string): boolean =>
  type.trim() === INTERNAL_SITE_TYPE;

/**
 * Returns whether the site details form can be saved.
 *
 * Type is always required. Internal sites need nothing else. External sites
 * also need NPI and Name.
 *
 * @param values - Current site details form values.
 * @returns True when the selected type's required fields are populated.
 */
export const isSiteDetailsFormValid = (
  values: SiteDetailsFormValues,
): boolean => {
  if (!values.type.trim()) {
    return false;
  }

  if (isInternalSiteType(values.type)) {
    return true;
  }

  return SITE_DETAILS_REQUIRED_FIELDS.every(
    (field) => !isBlankSiteDetailsValue(values[field]),
  );
};

/** User-facing validation messages for required site detail fields. */
const SITE_DETAILS_REQUIRED_FIELD_ERRORS: Partial<
  Record<keyof SiteDetailsFormValues, string>
> = {
  type: "Type is required",
  npi: "NPI is required",
  site_name: "Name is required",
};

/**
 * Returns the validation error for a site details field, if any.
 *
 * @param fieldKey - Site details form field key.
 * @param value - Current field value.
 * @returns Error message for empty required fields; otherwise an empty string.
 */
export const getSiteDetailsFieldError = (
  fieldKey: keyof SiteDetailsFormValues,
  value: string,
): string => {
  const requiredMessage = SITE_DETAILS_REQUIRED_FIELD_ERRORS[fieldKey];
  if (!requiredMessage || !isBlankSiteDetailsValue(value)) {
    return "";
  }

  return requiredMessage;
};

/**
 * Maps site details form values to the upsert API payload.
 *
 * A lone `"-"` is treated as empty so a display placeholder is not written
 * into the test-location catalog.
 *
 * @param values - Current site details form values.
 * @returns Request body for test location detail upsert.
 */
export const mapSiteDetailsFormToUpsertPayload = (
  values: SiteDetailsFormValues,
): UpsertTestLocationDetailsPayload => {
  const alias = blankIfMissingSiteDetailsPlaceholder(values.alias);

  return {
    site_name: blankIfMissingSiteDetailsPlaceholder(values.site_name),
    alias: alias.length > 0 ? [alias] : [],
    address: blankIfMissingSiteDetailsPlaceholder(values.address),
    city: blankIfMissingSiteDetailsPlaceholder(values.city),
    state: blankIfMissingSiteDetailsPlaceholder(values.state),
    zip: blankIfMissingSiteDetailsPlaceholder(values.zip),
    npi: blankIfMissingSiteDetailsPlaceholder(values.npi),
  };
};

/** Payload shape for POST /medical/frontend-query/update/submission. */
export interface SubmissionTestLocationUpdatePayload {
  /** Test location details persisted on the order submission row. */
  test_location_submission_details: TestLocationSubmissionPayload;
}

/**
 * Maps submission test location details to site details form values.
 *
 * Missing values and a stored `"-"` placeholder both become empty strings.
 *
 * @param details - Test location row from the submission API.
 * @returns Form values with empty strings for missing fields.
 */
export const mapTestLocationSubmissionDetailsToFormValues = (
  details: TestLocationSubmissionPayload | TestLocationSubmissionDetails,
): SiteDetailsFormValues => ({
  alias: blankIfMissingSiteDetailsPlaceholder(
    Array.isArray(details.alias)
      ? details.alias.join(", ")
      : (details.alias ?? ""),
  ),
  npi: blankIfMissingSiteDetailsPlaceholder(details.npi ?? ""),
  site_name: blankIfMissingSiteDetailsPlaceholder(details.site_name ?? ""),
  address: blankIfMissingSiteDetailsPlaceholder(
    details.address_details?.[0]?.address ?? "",
  ),
  city: blankIfMissingSiteDetailsPlaceholder(
    details.address_details?.[0]?.city ?? "",
  ),
  state: blankIfMissingSiteDetailsPlaceholder(
    details.address_details?.[0]?.state ?? "",
  ),
  zip: blankIfMissingSiteDetailsPlaceholder(
    details.address_details?.[0]?.zip ?? "",
  ),
  type: "",
});

/**
 * Resolves initial site details form values.
 *
 * Prefills ``alias`` from the first auth-on-file report's ``test_location``
 * when present; otherwise falls back to submission details (or empty defaults).
 * Remaining fields always come from submission details when available.
 *
 * @param details - Optional test location row from the submission API.
 * @param authOnFileDetails - Optional auth-on-file payload used for report alias.
 * @returns Form values with alias preferred from the first report.
 */
export const getSiteDetailsInitialFormValues = (
  details?: TestLocationSubmissionDetails | null,
  authOnFileDetails?: AuthOnFile,
): SiteDetailsFormValues => {
  const baseValues = details
    ? mapTestLocationSubmissionDetailsToFormValues(details)
    : EMPTY_SITE_DETAILS_FORM;
  const reportAlias =
    authOnFileDetails?.reports?.[0]?.test_location?.trim() ?? "";

  return {
    ...baseValues,
    alias: reportAlias || baseValues.alias,
  };
};

/** Default empty form state for site details. */
export const EMPTY_SITE_DETAILS_FORM: SiteDetailsFormValues = {
  alias: "",
  npi: "",
  site_name: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  type: "",
};
